import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import API_URL from "../../config/ApiUrl";
import { assertNotClientMode } from "../packing/packingModeGuard";

// ========== WORKSPACE REHYDRATION THUNK ==========
// Single entry point to load ALL package workspace data
// Fetches all required data in parallel, sets isPackageWorkspaceReady when complete
export const loadPackageWorkspace = createAsyncThunk(
  "packageWorkspace/loadPackageWorkspace",
  async (packageId, { rejectWithValue }) => {
    try {
      // Fetch package aggregated first to get salesOrderId
      const aggregatedResponse = await axios.get(API_URL.PACKAGE.AGGREGATED(packageId));
      const packageDetail = aggregatedResponse.data.data;
      const salesOrderId = packageDetail?.salesOrderId;

      // Fetch remaining data in parallel
      // IMPORTANT: Pass packageId to GET_PACKING_ITEMS to include items packed in this package
      // even if they are fully packed across all packages
      const [boxesResponse, packageItemsResponse, packingItemsResponse] = await Promise.all([
        axios.get(API_URL.BOX.BY_PACKAGE(packageId)),
        axios.get(API_URL.PACKAGE.GET_ITEMS(packageId)).catch(() => ({ data: { data: [] } })),
        salesOrderId
          ? axios.get(API_URL.SALES_ORDER.GET_PACKING_ITEMS(salesOrderId, packageId))
          : Promise.resolve({ data: { data: { items: [] } } }),
      ]);

      return {
        packageDetail,
        boxes: boxesResponse.data.data || [],
        packageItems: packageItemsResponse.data.data || [],
        packingItems: packingItemsResponse.data.data?.items || packingItemsResponse.data.data || [],
      };
    } catch (error) {
      return rejectWithValue(error.data || error.message);
    }
  }
);

// Fetch package aggregated data
export const getPackageAggregated = createAsyncThunk(
  "packageWorkspace/getPackageAggregated",
  async (packageId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.PACKAGE.AGGREGATED(packageId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Fetch boxes for a package
export const getPackageBoxes = createAsyncThunk(
  "packageWorkspace/getPackageBoxes",
  async (packageId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.BOX.BY_PACKAGE(packageId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Fetch packing items by salesOrderId (refreshes Items-to-Pack table)
export const getPackingItemsBySalesOrder = createAsyncThunk(
  "packageWorkspace/getPackingItemsBySalesOrder",
  async (salesOrderId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.SALES_ORDER.GET_PACKING_ITEMS(salesOrderId));
      return data.data?.items || data.data || [];
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Create a new box for a package
export const createBox = createAsyncThunk(
  "packageWorkspace/createBox",
  async (packageId, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(API_URL.BOX.CREATE(packageId));
      // Refresh boxes list after creation
      dispatch(getPackageBoxes(packageId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Close a box
export const closeBox = createAsyncThunk(
  "packageWorkspace/closeBox",
  async ({ boxId, packageId }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(API_URL.BOX.CLOSE(boxId));
      // Refresh boxes list after closing
      dispatch(getPackageBoxes(packageId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Delete a box
export const deleteBox = createAsyncThunk(
  "packageWorkspace/deleteBox",
  async ({ boxId, packageId }, { rejectWithValue, dispatch, getState }) => {
    try {
      const { data } = await axios.delete(API_URL.BOX.DELETE(boxId));

      // Get salesOrderId from state for packing items refresh
      const salesOrderId = getState().packageWorkspace.package_detail?.salesOrderId;

      // Refresh all package state after deletion
      dispatch(getPackageBoxes(packageId));
      dispatch(getPackageItems(packageId));
      dispatch(getPackageAggregated(packageId));

      // Refresh Items-to-Pack table (packing_items from SalesOrder API)
      if (salesOrderId) {
        dispatch(getPackingItemsBySalesOrder(salesOrderId));
      }

      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Fetch box detail
export const getBoxDetail = createAsyncThunk(
  "packageWorkspace/getBoxDetail",
  async (boxId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.BOX.GET_DETAIL(boxId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Add item to box
export const addItemToBox = createAsyncThunk(
  "packageWorkspace/addItemToBox",
  async ({ boxId, packageId, packedItemId, quantity }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(API_URL.BOX.ADD_ITEMS(boxId), {
        packedItemId,
        quantity,
      });
      // Refresh box detail, boxes list, and package aggregated data
      dispatch(getBoxDetail(boxId));
      dispatch(getPackageBoxes(packageId));
      dispatch(getPackageAggregated(packageId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Remove item from box
export const removeItemFromBox = createAsyncThunk(
  "packageWorkspace/removeItemFromBox",
  async ({ boxId, boxItemId, packageId }, { rejectWithValue, getState, dispatch }) => {
    try {
      // Get the item being deleted BEFORE the API call
      const state = getState();
      const boxItems = state.packageWorkspace.box_detail?.items || [];
      const deletedItem = boxItems.find((item) => item.id === boxItemId);

      const { data } = await axios.delete(API_URL.BOX.REMOVE_ITEM(boxId, boxItemId));

      // Refresh packing_items from authoritative backend source
      // This ensures Items-to-Pack UI shows correct packedQty / remainingToPackQty
      const salesOrderId = state.packageWorkspace.package_detail?.salesOrderId;
      if (salesOrderId) {
        dispatch(getPackingItemsBySalesOrder(salesOrderId));
      }

      // Return API response + deleted item info for state sync
      return {
        ...data.data,
        _deletedItem: deletedItem ? {
          boxItemId,
          sku: deletedItem.sku,
          quantity: deletedItem.quantity || 1,
          itemId: deletedItem.itemId,
        } : null,
      };
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Delete ALL BoxItems for a specific Item from a Box (by itemId, not boxItemId)
// Used when UI aggregates items by itemId and needs to delete all at once
export const deleteBoxItemsByItem = createAsyncThunk(
  "packageWorkspace/deleteBoxItemsByItem",
  async ({ boxId, itemId, packageId }, { rejectWithValue, getState, dispatch }) => {
    try {
      const { data } = await axios.delete(API_URL.BOX.REMOVE_ITEMS_BY_ITEM(boxId, itemId));

      // Refresh package items and packing items from authoritative backend source
      dispatch(getPackageItems(packageId));

      // Refresh packing_items (Items-to-Pack table)
      const state = getState();
      const salesOrderId = state.packageWorkspace.package_detail?.salesOrderId;
      if (salesOrderId) {
        dispatch(getPackingItemsBySalesOrder(salesOrderId));
      }

      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Scan QR code to add item to box
export const scanItemToBox = createAsyncThunk(
  "packageWorkspace/scanItemToBox",
  async ({ boxId, packageId, qrCode }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(API_URL.BOX.SCAN(boxId, packageId), {
        qrCode,
      });
      // Refresh box detail, boxes list, and package aggregated data
      dispatch(getBoxDetail(boxId));
      dispatch(getPackageBoxes(packageId));
      dispatch(getPackageAggregated(packageId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Complete package (with optional notes)
export const completePackage = createAsyncThunk(
  "packageWorkspace/completePackage",
  async ({ packageId, notes } = {}, { rejectWithValue }) => {
    try {
      const body = notes ? { notes } : undefined;
      const { data } = await axios.post(API_URL.PACKAGE.COMPLETE(packageId), body);
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// ========== PACKAGE DIRECT PACKING (WITHOUT BOX) ==========

// Fetch items packed directly to package (without box)
export const getPackageItems = createAsyncThunk(
  "packageWorkspace/getPackageItems",
  async (packageId, { rejectWithValue }) => {
    try {
      const { data } = await axios.get(API_URL.PACKAGE.GET_ITEMS(packageId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Add item directly to package (without box)
export const addItemToPackage = createAsyncThunk(
  "packageWorkspace/addItemToPackage",
  async ({ packageId, packedItemId, quantity }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(API_URL.PACKAGE.ADD_ITEM(packageId), {
        packedItemId,
        quantity,
      });
      // Refresh package items, boxes list, and package aggregated data
      dispatch(getPackageItems(packageId));
      dispatch(getPackageBoxes(packageId));
      dispatch(getPackageAggregated(packageId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Remove item from package (direct packed item without box)
export const removeItemFromPackage = createAsyncThunk(
  "packageWorkspace/removeItemFromPackage",
  async ({ packageId, packedItemId }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.delete(API_URL.PACKAGE.REMOVE_ITEM(packageId, packedItemId));
      // Refresh package items, boxes list, and package aggregated data
      dispatch(getPackageItems(packageId));
      dispatch(getPackageBoxes(packageId));
      dispatch(getPackageAggregated(packageId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// Scan QR code to add item directly to package (without box)
export const scanItemToPackage = createAsyncThunk(
  "packageWorkspace/scanItemToPackage",
  async ({ packageId, qrCode }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await axios.post(API_URL.PACKAGE.SCAN(packageId), {
        qrCode,
      });
      // Refresh package items, boxes list, and package aggregated data
      dispatch(getPackageItems(packageId));
      dispatch(getPackageBoxes(packageId));
      dispatch(getPackageAggregated(packageId));
      return data.data;
    } catch (error) {
      return rejectWithValue(error.data);
    }
  }
);

// ========== SERVER-SIDE PACKING MODE ==========

// Single scan for server-side packing mode
// Each scan triggers exactly ONE API call - no batching
// Response contains full updated state - NO follow-up GETs needed
//
// CRITICAL: This thunk must NEVER be dispatched in CLIENT packing mode.
// CLIENT mode uses clientEngine.scanSku() which is fully local.
export const serverSideScan = createAsyncThunk(
  "packageWorkspace/serverSideScan",
  async ({ packageId, boxId, sku, qty, mrp }, { rejectWithValue }) => {
    try {
      // SAFETY ASSERTION: Throw if called in CLIENT mode
      // This is the FINAL safety net - primary guards should be in components
      assertNotClientMode("serverSideScan thunk");

      console.log("[REDUX] serverSideScan dispatched (SERVER mode)", { packageId, boxId, sku, qty });

      // Use batch endpoint with single scan (as per API design)
      const { data } = await axios.post(API_URL.SCAN.BATCH, {
        packageId,
        boxId,
        scans: [{ sku, qty: qty || 1, mrp }],
      });
      // Return API response + scanned item info for box_detail.items update
      return {
        ...data.data,
        _scannedItem: { sku, qty: qty || 1, boxId },
      };
    } catch (error) {
      return rejectWithValue(error.data || error.message);
    }
  }
);

const initialState = {
  // WORKSPACE READINESS FLAG - must be true before rendering UI
  isPackageWorkspaceReady: false,
  workspace_loading: false,
  workspace_error: null,
  // Package data
  package_detail: null,
  items_to_pack: [],
  boxes: [],
  selected_box: null,
  box_detail: null,
  box_items: [],
  // Direct package packing (without box)
  package_items: [], // Items packed directly to package (no box)
  package_items_loading: false,
  package_item_action_loading: false,
  package_scan_loading: false,
  // Server-side packing mode (single scan per API call)
  server_scan_loading: false,
  last_scan_result: null, // Last successful scan result
  last_scan_error: null, // Last scan error
  // SINGLE SOURCE OF TRUTH for packing items (from scan response)
  packing_items: [], // Item-level packing data
  available_to_add: [], // Derived: packing_items.filter(i => i.remainingQty > 0)
  // Loading states
  loading: false,
  boxes_loading: false,
  box_action_loading: false,
  box_detail_loading: false,
  box_item_action_loading: false,
  scan_loading: false,
  complete_loading: false,
};

const packageWorkspaceSlice = createSlice({
  name: "packageWorkspace",
  initialState,
  reducers: {
    resetPackageWorkspace(state) {
      // Reset workspace readiness
      state.isPackageWorkspaceReady = false;
      state.workspace_loading = false;
      state.workspace_error = null;
      // Reset package data
      state.package_detail = null;
      state.items_to_pack = [];
      state.boxes = [];
      state.selected_box = null;
      state.box_detail = null;
      state.box_items = [];
      // Reset direct package packing state
      state.package_items = [];
      state.package_items_loading = false;
      state.package_item_action_loading = false;
      state.package_scan_loading = false;
      // Reset server-side scan state
      state.server_scan_loading = false;
      state.last_scan_result = null;
      state.last_scan_error = null;
      // Reset packing items (single source of truth)
      state.packing_items = [];
      state.available_to_add = [];
      // Reset loading states
      state.loading = false;
      state.boxes_loading = false;
      state.box_action_loading = false;
      state.box_detail_loading = false;
      state.box_item_action_loading = false;
      state.scan_loading = false;
      state.complete_loading = false;
    },
    setSelectedBox(state, action) {
      state.selected_box = action.payload;
    },
    clearSelectedBox(state) {
      state.selected_box = null;
      state.box_detail = null;
      state.box_items = [];
    },
    setPackageNotes(state, action) {
      if (state.package_detail) {
        state.package_detail.notes = action.payload;
      }
    },
    // Clear last scan error (for UI reset)
    clearLastScanError(state) {
      state.last_scan_error = null;
    },
    // Apply scan response directly to state (synchronous update)
    applyScanResponse(state, action) {
      const { package: pkg, box, items, scanSummary } = action.payload || {};

      // Update package detail
      if (pkg) {
        state.package_detail = {
          ...state.package_detail,
          ...pkg,
          status: pkg.status,
          totalPackedQty: pkg.totalPackedQty,
          isComplete: pkg.isComplete,
        };
      }

      // Update box detail if box data provided
      if (box) {
        state.box_detail = {
          ...state.box_detail,
          ...box,
          totalQty: box.totalQty,
        };
        // Also update selected_box if it matches
        if (state.selected_box && (state.selected_box.boxId === box.id || state.selected_box.id === box.id)) {
          state.selected_box = {
            ...state.selected_box,
            ...box,
            boxId: box.id,
            totalQty: box.totalQty,
          };
        }
      }

      // Update items_to_pack from response items
      if (Array.isArray(items)) {
        state.items_to_pack = items.map((item) => ({
          ...item,
          remainingToPackQty: item.remainingQty,
          orderedQty: item.orderedQty,
          packedQty: item.packedQty,
        }));
      }

      // Store scan summary for UI feedback
      state.last_scan_result = { scanSummary };
    },
    // Set packing_items from initial load (synced from packingSummary)
    setPackingItems(state, action) {
      const items = action.payload;
      if (Array.isArray(items)) {
        state.packing_items = items;
        // Recompute available_to_add immediately
        state.available_to_add = items.filter((item) => (item.remainingToPackQty ?? item.remainingQty ?? 0) > 0);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // ========== WORKSPACE REHYDRATION ==========
      .addCase(loadPackageWorkspace.pending, (state) => {
        state.isPackageWorkspaceReady = false;
        state.workspace_loading = true;
        state.workspace_error = null;
      })
      .addCase(loadPackageWorkspace.fulfilled, (state, action) => {
        const { packageDetail, boxes, packageItems, packingItems } = action.payload;

        // Set package detail
        state.package_detail = packageDetail;
        state.items_to_pack = packageDetail?.itemsToPack || [];

        // Set boxes
        state.boxes = boxes || [];

        // Set package items (items packed to package)
        state.package_items = packageItems || [];

        // Set packing items (SINGLE SOURCE OF TRUTH)
        const normalizedPackingItems = (packingItems || []).map((item) => ({
          ...item,
          remainingToPackQty: item.remainingToPackQty ?? item.remainingQty ?? 0,
          orderedQty: item.orderedQty ?? 0,
          packedQty: item.packedQty ?? 0,
        }));
        state.packing_items = normalizedPackingItems;
        state.available_to_add = normalizedPackingItems.filter(
          (item) => (item.remainingToPackQty ?? 0) > 0
        );

        // Mark workspace as ready
        state.isPackageWorkspaceReady = true;
        state.workspace_loading = false;
        state.workspace_error = null;
      })
      .addCase(loadPackageWorkspace.rejected, (state, action) => {
        state.isPackageWorkspaceReady = false;
        state.workspace_loading = false;
        state.workspace_error = action.payload || "Failed to load package workspace";
      })
      // Package aggregated
      .addCase(getPackageAggregated.pending, (state) => {
        state.loading = true;
      })
      .addCase(getPackageAggregated.fulfilled, (state, action) => {
        state.package_detail = action.payload;
        state.items_to_pack = action.payload?.itemsToPack || [];
        state.loading = false;
      })
      .addCase(getPackageAggregated.rejected, (state) => {
        state.loading = false;
      })
      // Package boxes
      .addCase(getPackageBoxes.pending, (state) => {
        state.boxes_loading = true;
      })
      .addCase(getPackageBoxes.fulfilled, (state, action) => {
        state.boxes = action.payload || [];
        state.boxes_loading = false;
      })
      .addCase(getPackageBoxes.rejected, (state) => {
        state.boxes_loading = false;
      })
      // Packing items by salesOrderId (refreshes Items-to-Pack table)
      .addCase(getPackingItemsBySalesOrder.fulfilled, (state, action) => {
        const items = action.payload || [];
        const normalizedItems = items.map((item) => ({
          ...item,
          remainingToPackQty: item.remainingToPackQty ?? item.remainingQty ?? 0,
          orderedQty: item.orderedQty ?? 0,
          packedQty: item.packedQty ?? 0,
        }));
        state.packing_items = normalizedItems;
        state.available_to_add = normalizedItems.filter((item) => (item.remainingToPackQty ?? 0) > 0);
        state.items_to_pack = normalizedItems;
      })
      // Create box
      .addCase(createBox.pending, (state) => {
        state.box_action_loading = true;
      })
      .addCase(createBox.fulfilled, (state) => {
        state.box_action_loading = false;
      })
      .addCase(createBox.rejected, (state) => {
        state.box_action_loading = false;
      })
      // Close box
      .addCase(closeBox.pending, (state) => {
        state.box_action_loading = true;
      })
      .addCase(closeBox.fulfilled, (state) => {
        state.box_action_loading = false;
      })
      .addCase(closeBox.rejected, (state) => {
        state.box_action_loading = false;
      })
      // Delete box
      .addCase(deleteBox.pending, (state) => {
        state.box_action_loading = true;
      })
      .addCase(deleteBox.fulfilled, (state) => {
        state.box_action_loading = false;
      })
      .addCase(deleteBox.rejected, (state) => {
        state.box_action_loading = false;
      })
      // Get box detail
      .addCase(getBoxDetail.pending, (state) => {
        state.box_detail_loading = true;
      })
      .addCase(getBoxDetail.fulfilled, (state, action) => {
        state.box_detail = action.payload;
        state.box_items = action.payload?.items || [];
        state.box_detail_loading = false;
      })
      .addCase(getBoxDetail.rejected, (state) => {
        state.box_detail_loading = false;
      })
      // Add item to box
      .addCase(addItemToBox.pending, (state) => {
        state.box_item_action_loading = true;
      })
      .addCase(addItemToBox.fulfilled, (state) => {
        state.box_item_action_loading = false;
      })
      .addCase(addItemToBox.rejected, (state) => {
        state.box_item_action_loading = false;
      })
      // Remove item from box
      .addCase(removeItemFromBox.pending, (state) => {
        state.box_item_action_loading = true;
      })
      .addCase(removeItemFromBox.fulfilled, (state, action) => {
        state.box_item_action_loading = false;

        const { _deletedItem } = action.payload || {};

        if (_deletedItem) {
          const { boxItemId, sku, quantity, itemId } = _deletedItem;

          // 1. Remove item from box_detail.items
          if (Array.isArray(state.box_detail?.items)) {
            state.box_detail.items = state.box_detail.items.filter(
              (item) => item.id !== boxItemId
            );
          }

          // 2. Update box_items for backwards compatibility
          if (Array.isArray(state.box_items)) {
            state.box_items = state.box_items.filter(
              (item) => item.id !== boxItemId
            );
          }

          // 3. Update packing_items: decrease packedQty, increase remainingToPackQty
          if (Array.isArray(state.packing_items) && sku) {
            state.packing_items = state.packing_items.map((item) => {
              if (item.sku?.toLowerCase() === sku.toLowerCase() || item.itemId === itemId) {
                const currentPacked = item.packedQty ?? 0;
                const currentRemaining = item.remainingToPackQty ?? 0;
                const deletedQty = quantity || 1;

                return {
                  ...item,
                  packedQty: Math.max(0, currentPacked - deletedQty),
                  remainingToPackQty: currentRemaining + deletedQty,
                };
              }
              return item;
            });

            // 4. Recompute available_to_add
            state.available_to_add = state.packing_items.filter(
              (item) => (item.remainingToPackQty ?? 0) > 0
            );

            // 5. Also update items_to_pack for backwards compatibility
            state.items_to_pack = state.packing_items;
          }

          // 6. Update selected_box.items if it matches
          if (state.selected_box && Array.isArray(state.selected_box.items)) {
            state.selected_box.items = state.selected_box.items.filter(
              (item) => item.id !== boxItemId
            );
          }
        }
      })
      .addCase(removeItemFromBox.rejected, (state) => {
        state.box_item_action_loading = false;
      })
      // Scan item to box
      .addCase(scanItemToBox.pending, (state) => {
        state.scan_loading = true;
      })
      .addCase(scanItemToBox.fulfilled, (state) => {
        state.scan_loading = false;
      })
      .addCase(scanItemToBox.rejected, (state) => {
        state.scan_loading = false;
      })
      // Complete package
      .addCase(completePackage.pending, (state) => {
        state.complete_loading = true;
      })
      .addCase(completePackage.fulfilled, (state) => {
        state.complete_loading = false;
      })
      .addCase(completePackage.rejected, (state) => {
        state.complete_loading = false;
      })
      // ========== PACKAGE DIRECT PACKING (WITHOUT BOX) ==========
      // Get package items (packed directly to package)
      .addCase(getPackageItems.pending, (state) => {
        state.package_items_loading = true;
      })
      .addCase(getPackageItems.fulfilled, (state, action) => {
        state.package_items = action.payload || [];
        state.package_items_loading = false;
      })
      .addCase(getPackageItems.rejected, (state) => {
        state.package_items_loading = false;
      })
      // Add item directly to package
      .addCase(addItemToPackage.pending, (state) => {
        state.package_item_action_loading = true;
      })
      .addCase(addItemToPackage.fulfilled, (state) => {
        state.package_item_action_loading = false;
      })
      .addCase(addItemToPackage.rejected, (state) => {
        state.package_item_action_loading = false;
      })
      // Remove item from package
      .addCase(removeItemFromPackage.pending, (state) => {
        state.package_item_action_loading = true;
      })
      .addCase(removeItemFromPackage.fulfilled, (state) => {
        state.package_item_action_loading = false;
      })
      .addCase(removeItemFromPackage.rejected, (state) => {
        state.package_item_action_loading = false;
      })
      // Scan item to package
      .addCase(scanItemToPackage.pending, (state) => {
        state.package_scan_loading = true;
      })
      .addCase(scanItemToPackage.fulfilled, (state) => {
        state.package_scan_loading = false;
      })
      .addCase(scanItemToPackage.rejected, (state) => {
        state.package_scan_loading = false;
      })
      // ========== SERVER-SIDE PACKING MODE (SINGLE SCAN) ==========
      .addCase(serverSideScan.pending, (state) => {
        state.server_scan_loading = true;
        state.last_scan_error = null;
      })
      .addCase(serverSideScan.fulfilled, (state, action) => {
        state.server_scan_loading = false;
        state.last_scan_error = null;

        // Apply scan response synchronously - NO follow-up GETs needed
        // Response structure: { packageId, boxId, items: ScannedBoxItemDto[] }
        const { package: pkg, box, items, scanSummary, boxId: responseBoxId } = action.payload || {};

        // Update package detail
        if (pkg) {
          state.package_detail = {
            ...state.package_detail,
            ...pkg,
            status: pkg.status,
            totalPackedQty: pkg.totalPackedQty,
            isComplete: pkg.isComplete,
          };
        }

        // =================================================================
        // STEP 2: UPDATE packing_items using REAL backend values
        // For each ScannedBoxItemDto, update packedQty and remainingToPackQty
        // =================================================================
        if (Array.isArray(items) && items.length > 0) {
          const existingPackingItems = Array.isArray(state.packing_items) ? state.packing_items : [];

          // Create lookup map from response items by itemId
          const responseItemsMap = {};
          items.forEach((dto) => {
            if (dto.itemId) {
              responseItemsMap[dto.itemId] = dto;
            }
          });

          // Update packing_items with backend values (trust backend, no UI recompute)
          const updatedPackingItems = existingPackingItems.map((packingItem) => {
            const dto = responseItemsMap[packingItem.itemId];
            if (dto) {
              return {
                ...packingItem,
                packedQty: dto.packedQty ?? packingItem.packedQty ?? 0,
                remainingToPackQty: dto.remainingToPackQty ?? packingItem.remainingToPackQty ?? 0,
              };
            }
            return packingItem;
          });

          state.packing_items = updatedPackingItems;
          state.available_to_add = updatedPackingItems.filter((item) => (item.remainingToPackQty ?? 0) > 0);
          state.items_to_pack = updatedPackingItems;
        }

        // =================================================================
        // STEP 1: UPDATE package_items using REAL boxItemId from response
        // NO temp IDs - use only IDs returned from scan/batch API
        // =================================================================
        if (Array.isArray(items) && items.length > 0) {
          const boxId = responseBoxId || box?.id;
          const existingPackageItems = Array.isArray(state.package_items) ? state.package_items : [];

          // Process each item from response
          items.forEach((dto) => {
            // boxItemId is the REAL database ID for delete operations
            const realBoxItemId = dto.boxItemId;
            if (!realBoxItemId) return; // Skip if no real ID

            // Find existing item by boxItemId (unique identifier)
            const existingIndex = existingPackageItems.findIndex((item) => item.id === realBoxItemId);

            if (existingIndex >= 0) {
              // Update existing item with new quantity
              existingPackageItems[existingIndex] = {
                ...existingPackageItems[existingIndex],
                quantity: dto.totalQuantityInBox ?? existingPackageItems[existingIndex].quantity,
                packedItemId: dto.packedItemId,
              };
            } else {
              // Append new item using REAL IDs from response
              existingPackageItems.push({
                id: realBoxItemId,              // REAL boxItemId for DELETE API
                packedItemId: dto.packedItemId, // Preserve packedItemId
                itemId: dto.itemId,
                sku: dto.sku,
                itemName: dto.itemName,
                quantity: dto.totalQuantityInBox ?? dto.quantityAdded ?? 1,
                boxId: boxId,
                mrp: dto.mrp ?? null,
              });
            }
          });

          state.package_items = existingPackageItems;
        }

        // Update box detail if box data provided
        if (box) {
          const boxId = responseBoxId || box.id;

          // Build box items from package_items filtered by boxId
          const boxItems = (Array.isArray(state.package_items) ? state.package_items : [])
            .filter((item) => item.boxId === boxId)
            .map((item) => ({
              id: item.id,           // REAL boxItemId
              itemId: item.itemId,
              sku: item.sku,
              itemName: item.itemName,
              quantity: item.quantity,
              mrp: item.mrp,
            }));

          state.box_detail = {
            ...state.box_detail,
            ...box,
            totalQty: box.totalQty,
            items: boxItems,
          };

          // Also update selected_box if it matches
          if (state.selected_box && (state.selected_box.boxId === box.id || state.selected_box.id === box.id)) {
            state.selected_box = {
              ...state.selected_box,
              ...box,
              boxId: box.id,
              totalQty: box.totalQty,
              items: boxItems,
            };
          }

          // Also update box_items for backwards compatibility
          state.box_items = boxItems;
        }

        // Store scan summary for UI feedback
        state.last_scan_result = { scanSummary };
      })
      .addCase(serverSideScan.rejected, (state, action) => {
        state.server_scan_loading = false;
        state.last_scan_error = action.payload;
      });
  },
});

export const { resetPackageWorkspace, setSelectedBox, clearSelectedBox, clearLastScanError, applyScanResponse, setPackingItems, setPackageNotes } = packageWorkspaceSlice.actions;

export default packageWorkspaceSlice.reducer;
